import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";

type InquiryFormSectionProps = {
  apiBaseUrl: string;
  turnstileSiteKey?: string;
  source: "company" | "contact";
  imageSrc: string;
  imageAlt: string;
  partners: string[];
};

type ContactSubmitResponse =
  | {
      success?: boolean;
      data?: {
        message?: string;
      };
      error?: string;
    }
  | null;

type TurnstileRenderOptions = {
  sitekey: string;
  action?: string;
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
};

type TurnstileApi = {
  render: (container: string | HTMLElement, options: TurnstileRenderOptions) => string;
  remove: (widgetId?: string) => void;
};

const TURNSTILE_ACTION = "contact_form_submit";
const phoneCountryOptions = [
  ["US", "+1"],
  ["CA", "+1"],
  ["MX", "+52"],
  ["BR", "+55"],
  ["AR", "+54"],
  ["CO", "+57"],
  ["PE", "+51"],
  ["CL", "+56"],
  ["UK", "+44"],
  ["IE", "+353"],
  ["FR", "+33"],
  ["ES", "+34"],
  ["IT", "+39"],
  ["DE", "+49"],
  ["NL", "+31"],
  ["BE", "+32"],
  ["CH", "+41"],
  ["AT", "+43"],
  ["SE", "+46"],
  ["NO", "+47"],
  ["DK", "+45"],
  ["FI", "+358"],
  ["PL", "+48"],
  ["CZ", "+420"],
  ["GR", "+30"],
  ["TR", "+90"],
  ["KZ", "+7"],
  ["RU", "+7"],
  ["EG", "+20"],
  ["ZA", "+27"],
  ["AE", "+971"],
  ["SA", "+966"],
  ["IL", "+972"],
  ["IN", "+91"],
  ["PK", "+92"],
  ["BD", "+880"],
  ["JP", "+81"],
  ["KR", "+82"],
  ["CN", "+86"],
  ["HK", "+852"],
  ["MO", "+853"],
  ["TW", "+886"],
  ["PH", "+63"],
  ["MY", "+60"],
  ["SG", "+65"],
  ["TH", "+66"],
  ["VN", "+84"],
  ["ID", "+62"],
  ["AU", "+61"],
  ["NZ", "+64"]
] as const;

function getTurnstile(): TurnstileApi | undefined {
  return (window as Window & { turnstile?: TurnstileApi }).turnstile;
}

export default function InquiryFormSection({
  apiBaseUrl,
  turnstileSiteKey,
  source,
  imageSrc,
  imageAlt,
  partners
}: InquiryFormSectionProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phonePrefix, setPhonePrefix] = useState("+1");
  const [phone, setPhone] = useState("");
  const [whatsApp, setWhatsApp] = useState("");
  const [message, setMessage] = useState("");
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileVisible, setTurnstileVisible] = useState(false);
  const [submitPending, setSubmitPending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const widgetContainerId = useMemo(
    () => `contact-turnstile-${source}`,
    [source]
  );
  const isEmailValid = useMemo(() => /^\S+@\S+\.\S+$/.test(email.trim()), [email]);

  useEffect(() => {
    if (!turnstileSiteKey) {
      return;
    }

    if (getTurnstile()) {
      setTurnstileReady(true);
      return;
    }

    const existed = document.querySelector<HTMLScriptElement>(
      'script[data-turnstile-script="true"]'
    );
    const onLoaded = () => {
      setTurnstileReady(true);
    };

    if (existed) {
      existed.addEventListener("load", onLoaded);
      if (getTurnstile()) {
        setTurnstileReady(true);
      }
      return () => {
        existed.removeEventListener("load", onLoaded);
      };
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.setAttribute("data-turnstile-script", "true");
    script.addEventListener("load", onLoaded);
    document.head.appendChild(script);

    return () => {
      script.removeEventListener("load", onLoaded);
    };
  }, [turnstileSiteKey]);

  const submitByToken = useCallback(
    async (turnstileToken: string) => {
      setSubmitPending(true);
      setSubmitError(null);
      setSubmitSuccess(null);
      try {
        const response = await fetch(`${apiBaseUrl}/api/public/contact/submit`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            source,
            name: name.trim(),
            email: email.trim(),
            phonePrefix,
            phone: phone.trim(),
            whatsApp: whatsApp.trim(),
            message: message.trim(),
            turnstileToken
          })
        });

        const payload = (await response.json().catch(() => null)) as ContactSubmitResponse;

        if (!response.ok || !payload?.success || !payload.data) {
          throw new Error(payload?.error || "Failed to submit your request.");
        }

        setSubmitSuccess(
          payload.data.message ||
            "Thanks for reaching out. We have sent your request to our team."
        );
        setMessage("");
      } catch (error) {
        setSubmitError(
          error instanceof Error ? error.message : "Failed to submit your request."
        );
      } finally {
        setTurnstileVisible(false);
        setSubmitPending(false);
        const turnstile = getTurnstile();
        if (turnstile && widgetIdRef.current) {
          turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        }
      }
    },
    [apiBaseUrl, email, message, name, phone, phonePrefix, source, whatsApp]
  );

  useEffect(() => {
    if (!turnstileVisible) {
      const turnstile = getTurnstile();
      if (turnstile && widgetIdRef.current) {
        turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
      return;
    }

    const turnstile = getTurnstile();
    if (!turnstile || !turnstileReady || !turnstileSiteKey) {
      return;
    }

    if (widgetIdRef.current) {
      return;
    }

    const widgetId = turnstile.render(`#${widgetContainerId}`, {
      sitekey: turnstileSiteKey,
      action: TURNSTILE_ACTION,
      callback: (token: string) => {
        void submitByToken(token);
      },
      "expired-callback": () => {
        setSubmitError("Bot verification expired. Please try again.");
      },
      "error-callback": () => {
        setSubmitError("Bot verification failed to load. Please refresh and try again.");
      }
    });

    widgetIdRef.current = widgetId;
  }, [
    submitByToken,
    turnstileReady,
    turnstileSiteKey,
    turnstileVisible,
    widgetContainerId
  ]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);
    setSubmitSuccess(null);

    if (!name.trim()) {
      setSubmitError("Please enter your name.");
      return;
    }

    if (!isEmailValid) {
      setSubmitError("Please enter a valid email address.");
      return;
    }

    if (!turnstileSiteKey) {
      setSubmitError("Bot verification is not configured.");
      return;
    }

    if (submitPending) {
      return;
    }

    setTurnstileVisible(true);
  };

  return (
    <div className="mt-10 overflow-hidden rounded-3xl bg-white shadow-[0_24px_50px_rgba(15,23,36,0.12)]">
      <div className="grid gap-0 lg:grid-cols-[1fr_0.9fr]">
        <div className="h-full">
          <img src={imageSrc} alt={imageAlt} className="h-full w-full object-cover" />
        </div>
        <div className="bg-[#f8fafc] px-8 py-10">
          <h4 className="text-xl font-semibold text-[#111827]">Plan your Next Trip</h4>
          <p className="mt-3 text-sm leading-relaxed text-[#6b7280]">
            Name and email are required. All other fields are optional.
          </p>
          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="Type your name *"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="w-full rounded-md border border-[#e5e7eb] bg-white px-4 py-3 text-sm outline-none"
            />
            <input
              type="email"
              placeholder="Insert your email *"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full rounded-md border border-[#e5e7eb] bg-white px-4 py-3 text-sm outline-none"
            />
            <div className="grid gap-3 md:grid-cols-[140px_1fr]">
              <select
                value={phonePrefix}
                onChange={(event) => setPhonePrefix(event.target.value)}
                className="h-11 w-full rounded-md border border-[#e5e7eb] bg-white px-3 text-sm outline-none"
                aria-label="Phone prefix"
              >
                {phoneCountryOptions.map(([countryCode, dialCode]) => (
                  <option key={`${countryCode}-${dialCode}`} value={dialCode}>
                    {countryCode} {dialCode}
                  </option>
                ))}
              </select>
              <input
                type="tel"
                placeholder="Contact phone"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                className="h-11 w-full rounded-md border border-[#e5e7eb] bg-white px-4 text-sm outline-none"
              />
            </div>
            <input
              type="text"
              placeholder="WhatsApp"
              value={whatsApp}
              onChange={(event) => setWhatsApp(event.target.value)}
              className="w-full rounded-md border border-[#e5e7eb] bg-white px-4 py-3 text-sm outline-none"
            />
            <textarea
              placeholder="Your Message"
              rows={4}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              className="w-full rounded-md border border-[#e5e7eb] bg-white px-4 py-3 text-sm outline-none"
            />

            {turnstileVisible && (
              <div className="rounded-xl border border-[#e5e7eb] bg-[#ffffff] p-3">
                <p className="text-xs text-[#6b7280]">
                  Complete bot verification to submit.
                </p>
                <div id={widgetContainerId} className="mt-3 min-h-[66px]" />
              </div>
            )}

            {submitError && <p className="text-xs text-[#ef4444]">{submitError}</p>}
            {submitSuccess && <p className="text-xs text-[#2563eb]">{submitSuccess}</p>}

            <button
              type="submit"
              disabled={submitPending}
              className="w-full rounded-md bg-[#f3b455] px-4 py-3 text-sm font-semibold text-[#1d1d1d] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitPending ? "Submitting..." : "Submit"}
            </button>
          </form>
        </div>
      </div>
      <div className="bg-[#0a1d46] px-6 py-6">
        <div className="grid gap-4 text-center text-xs uppercase tracking-[0.35em] text-white/80 md:grid-cols-6 md:text-left md:tracking-[0.25em]">
          {partners.map((item, index) => (
            <span key={`${item}-${index}`}>{item}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
